import { useTranslation } from 'next-i18next'
import Image from 'next/image'
import WhatsappIcon from 'public/svgs/whatsapp-icon.svg'
import React, { useEffect, useRef, useState } from 'react'
import { buildLeadFormWaMessage } from 'src/components/LeadForm/buildLeadFormWaMessage'
import useTrackEvent from 'src/hooks/useTrackEvent'
import { DealType, ILocation } from 'src/types/graphql'
import { runAfterNextPaint } from 'src/utils/afterNextPaint'
import useAddToCart, { useGenerateLead } from 'src/utils/pixels/common'
import { trackSearchCardWhatsappClick } from 'src/utils/pixels/searchEvents'
import { firedOncePerSession } from 'src/utils/pixels/sessionDedup'
import { getClickIdsFromCookies } from 'src/utils/utms'
import { claimWaSessionToken } from 'src/utils/waSessionToken'
import { buildWhatsappUrl } from 'src/utils/whatsapp'

/** The two off-platform deal types whose search card carries the pill. */
export type SearchCardWhatsappDealType = DealType.Traditional | DealType.Broker

interface SearchCardWhatsappButtonProps {
  location: ILocation
  dealType: SearchCardWhatsappDealType
  /** 1-based rank in the result list — rides the discovery event. */
  position?: number
  /** Search zone (city slug) — same dimension as search_result_click. */
  zone?: string
}

/**
 * WhatsApp pill on Traditional / Broker search-grid cards (origination levers
 * chunk A, 2026-10-16) — lets a renter open the corporate WhatsApp from the
 * grid without opening the listing (69% of search sessions never do).
 *
 * The click mirrors `PriceCard.handleOnWhatsappCta` EXACTLY, including its
 * session dedupe key `wa_cta:<configId>:<subEvent>`, so a card tap and a later
 * listing-page tap on the same configuration count as ONE conversion. Only the
 * `search_card_whatsapp_click` discovery event fires on every tap.
 *
 * Mounted as a child of the card's `<Link>`, so the handler must
 * `preventDefault` (no navigation) + `stopPropagation` (no
 * `search_result_click`, no mobile `doNavigate`). Pre-hydration a tap falls
 * through to the anchor and opens the listing, which is the right fallback.
 *
 * Hydration-safe: render reads nothing client-only; the session token and the
 * click-id cookies are read inside the handler.
 *
 * Visual: icon-only below `xl:` (the search rail is 45% of the viewport, so
 * the copy column is ~134px at 320 and ~228px at 1024 — a labelled pill would
 * squeeze the price), labelled from `xl:`. On mobile the 28px circle carries a
 * `before:` hit area that brings the tap target to 44px. On the stacked card
 * (`search_card_stacked` test arm) the full-width copy column has room, so it
 * is a labelled 36px pill instead of an icon-only dot. `green-800`, not
 * the listing pill's `green-500`: white on #5BE793 is ~1.6:1, on #009E6B
 * ~3.4:1 (clears the 3:1 non-text bar for the glyph; the accessible name is
 * the aria-label).
 */
const SearchCardWhatsappButton = ({
  location,
  dealType,
  position,
  zone
}: SearchCardWhatsappButtonProps) => {
  const { t } = useTranslation('common')
  const { trackEvent, EventAction } = useTrackEvent()
  const handleAddToCart = useAddToCart()
  const generateLead = useGenerateLead()
  const config = location.defaultConfiguration ?? null
  const ref = useRef<HTMLButtonElement>(null)
  // 'unknown' on SSR + first paint (invisible, layout reserved — no
  // hydration mismatch, no shift); measured after mount. See usePillFit.
  const fits = usePillFit(ref, `${config?.id ?? ''}:${config?.price ?? ''}`)

  const handleClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e.preventDefault()
    e.stopPropagation()

    const isTraditional = dealType === DealType.Traditional
    const subEvent: 'broker_contact' | 'traditional_contact' = isTraditional
      ? 'traditional_contact'
      : 'broker_contact'
    const method = isTraditional ? 'traditional_contact' : 'broker'

    // Same key as the listing page's WA pill (PriceCard) — see header.
    const waDedupKey = `wa_cta:${config?.id ?? 'none'}:${subEvent}`
    // After the tap's paint, like PriceCard (Jules, 2026-10-28); WhatsApp
    // opening hides the page, which flushes this — see runAfterNextPaint.
    runAfterNextPaint(() => {
      trackSearchCardWhatsappClick({
        surface: 'search_card',
        dealType,
        locationId: location.id,
        configId: config?.id,
        position,
        zone
      })
      if (firedOncePerSession(waDedupKey)) return
      handleAddToCart(subEvent, location, config)
      trackEvent({
        action: EventAction.BrokerSignup,
        locationId: location.id,
        configurationId: config?.id,
        subType: subEvent
      })
      // `intentOnly: true` — a WA tap is upstream INTENT; the WhatsApp
      // primary is the server-side `qualified_whatsapp_lead`.
      generateLead({
        method,
        locationType: config?.locationType ?? undefined,
        dealType,
        monthlyPrice: config?.price ?? undefined,
        channel: 'whatsapp',
        intentOnly: true
      })
    })

    // The card links to the search-matched configuration's route, so the
    // prefill does too — its config UUID is what the chatbot's
    // `parseListingRefFromText` resolves the listing from.
    const msg = buildLeadFormWaMessage({
      location: {
        ...location,
        urlRoute: config?.urlRoute ?? location.urlRoute
      },
      configuration: config,
      values: {},
      locale: 'es',
      sessionToken: claimWaSessionToken(),
      clickIds: getClickIdsFromCookies()
    })
    window.open(buildWhatsappUrl(msg), '_blank')
  }

  return (
    <button
      ref={ref}
      type="button"
      onClick={handleClick}
      aria-label={t('SEARCH_CARD.WHATSAPP_CTA_ARIA')}
      className={`${fits ? 'visible' : 'invisible'} relative inline-flex size-7 shrink-0 items-center justify-center gap-1.5 rounded-full bg-green-800 text-xs font-semibold whitespace-nowrap text-white-500 shadow-[0_2px_8px_-2px_rgba(18,38,63,0.25)] transition-[background-color,transform] duration-150 before:absolute before:-inset-2 before:content-[''] hover:bg-green-700 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-green-800 active:scale-95 md:size-8 md:before:inset-0 xl:h-8 xl:w-auto xl:px-3 card-stacked:h-9 card-stacked:w-auto card-stacked:px-3.5 card-stacked:text-sm card-stacked:before:-inset-1`}
    >
      <Image
        src={WhatsappIcon}
        alt=""
        width={16}
        height={16}
        aria-hidden="true"
        className="size-4"
      />
      <span className="hidden xl:inline card-stacked:inline" aria-hidden="true">
        {t('SEARCH_CARD.WHATSAPP_CTA')}
      </span>
    </button>
  )
}

/** Clear space (px) kept between the price text and the pill. */
const PILL_FIT_MARGIN_PX = 4

/**
 * Whether the pill fits beside the card's price without covering it.
 *
 * The price span is `min-w-0` and its amount is `whitespace-nowrap`, so when
 * the row is tight the amount does not shrink — it runs UNDER the pill.
 * Measured 2026-10-16 (Monterrey grid, prod data): six-digit prices collide
 * at 320px and 1024–1279px, five-digit ones at 900–1023px, and «Desde»
 * wrapping makes the boundary non-monotonic, so no breakpoint/digit rule is
 * safe. The pill therefore measures: it stays in layout (so the row never
 * reflows) and only becomes visible when the amount's right edge plus a
 * margin clears the pill's left edge. `visibility: hidden` also removes it
 * from taps and the accessibility tree; the card still opens the listing,
 * whose own WhatsApp CTA is always present.
 *
 * Re-measures on row resize (rotation, rail width) and once web fonts load
 * (Rubik swaps in wider than the fallback). Inline spans can't be observed by
 * ResizeObserver, hence the row + `document.fonts.ready`.
 */
function usePillFit(
  ref: React.RefObject<HTMLButtonElement | null>,
  /** Re-measure when the card's configuration or price changes in place. */
  contentKey: string
): boolean {
  const [fits, setFits] = useState(false)

  useEffect(() => {
    const pill = ref.current
    const row = pill?.parentElement
    if (!pill || !row) return
    const amount = row.querySelector<HTMLElement>('[data-card-amount]')

    const measure = () => {
      if (!amount) {
        setFits(true)
        return
      }
      const amountRight = amount.getBoundingClientRect().right
      const pillLeft = pill.getBoundingClientRect().left
      setFits(amountRight + PILL_FIT_MARGIN_PX <= pillLeft)
    }

    measure()
    let cancelled = false
    document.fonts?.ready
      .then(() => {
        if (!cancelled) measure()
      })
      .catch(() => undefined)
    if (typeof ResizeObserver === 'undefined') {
      return () => {
        cancelled = true
      }
    }
    const observer = new ResizeObserver(measure)
    observer.observe(row)
    return () => {
      cancelled = true
      observer.disconnect()
    }
  }, [ref, contentKey])

  return fits
}

export default SearchCardWhatsappButton
